import React, { useState, useRef } from 'react';
import { X, Upload, Loader2 } from 'lucide-react';

interface QuestionImageUploadProps {
  questionIndex: number;
  onImageUpload: (index: number, imageData: {path: string, url: string, file: File}) => void;
  onImageRemove: (questionIndex: number, imageIndex: number) => void;
  existingImageUrls?: string[];
  disabled?: boolean;
  maxImages?: number;
}

const API_URL = import.meta.env.VITE_API_URL || '';

const QuestionImageUpload: React.FC<QuestionImageUploadProps> = ({
  questionIndex,
  onImageUpload,
  onImageRemove,
  existingImageUrls = [],
  disabled = false,
  maxImages = 5
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (files: File | File[]) => {
    const fileArray = Array.isArray(files) ? files : [files];
    
    // Check if max images would be exceeded
    const availableSlots = maxImages - existingImageUrls.length;
    if (availableSlots <= 0) {
      setError(`Maximum of ${maxImages} images per question reached`);
      return;
    }
    
    // Take only as many files as we have slots
    const filesToUpload = fileArray.slice(0, availableSlots);
    
    if (filesToUpload.length < fileArray.length) {
      setError(`Only ${filesToUpload.length} of ${fileArray.length} images added (max ${maxImages} per question)`);
    } else {
      setError(null);
    }
    
    // Validate all files
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 15 * 1024 * 1024; // 15MB
    
    for (const file of filesToUpload) {
      if (!validTypes.includes(file.type)) {
        setError(`Invalid file type: ${file.name}. Please upload PNG, JPG, GIF or WEBP`);
        return;
      }
      
      if (file.size > maxSize) {
        setError(`File ${file.name} exceeds 15MB limit`);
        return;
      }
    }

    setUploading(true);

    try {
      // Upload files sequentially to maintain order
      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_URL}/api/question-images/upload`, {
          method: 'POST',
          body: formData,
          credentials: 'include'
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || errorData.detail || 'Upload failed');
        }

        const data = await response.json();
        onImageUpload(questionIndex, {
          path: data.image_path,
          url: data.image_url,
          file: file
        });
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(Array.from(files));
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(Array.from(files));
    }
  };

  const handleRemove = async (imageIndex: number) => {
    onImageRemove(questionIndex, imageIndex);
    setError(null);
  };

  const handleClick = () => {
    if (!disabled && existingImageUrls.length < maxImages) {
      fileInputRef.current?.click();
    }
  };

  const canAddMore = existingImageUrls.length < maxImages;

  return (
    <div className="mt-2">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled}
        multiple
      />

      {existingImageUrls.length === 0 ? (
        /* Large drop zone when no images */
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            h-24 w-full border-2 border-dashed rounded-lg cursor-pointer transition-all flex flex-col items-center justify-center gap-2
            ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            ${uploading ? 'pointer-events-none' : ''}
          `}
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          ) : (
            <>
              <Upload className="w-6 h-6 text-gray-400" />
              <div className="text-center">
                <p className="text-sm text-gray-600">Drop images here or click to upload</p>
                <p className="text-xs text-gray-500 mt-1">Max {maxImages} images, up to 15MB each</p>
              </div>
            </>
          )}
        </div>
      ) : (
        /* Grid layout with images and add more tile */
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {existingImageUrls.map((imageUrl, index) => (
            <div key={index} className="relative group">
              <img
                src={`${API_URL}${imageUrl}`}
                alt={`Question ${questionIndex + 1} - Image ${index + 1}`}
                className="h-28 w-full object-cover rounded-md border border-gray-300 transition-transform group-hover:scale-[1.02]"
              />
              <button
                onClick={() => handleRemove(index)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-colors shadow-md"
                disabled={disabled}
                title="Remove image"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          
          {/* Add more tile */}
          {canAddMore && (
            <div
              onClick={handleClick}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`
                h-28 border-2 border-dashed rounded-md cursor-pointer transition-all flex flex-col items-center justify-center gap-2
                ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                ${uploading ? 'pointer-events-none' : ''}
              `}
            >
              {uploading ? (
                <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
              ) : (
                <>
                  <Upload className="w-5 h-5 text-gray-400" />
                  <div className="text-center px-2">
                    <p className="text-xs text-gray-600">Add more</p>
                    <p className="text-[10px] text-gray-500">
                      {existingImageUrls.length}/{maxImages}
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-1 text-xs text-red-600">
          {error}
        </div>
      )}
    </div>
  );
};

export default QuestionImageUpload;

